/**
 * Auth "middleware" for custom server.
 *
 * @todo tests, this is an important module.
 */
import { request } from 'graphql-request';
import { IncomingHttpHeaders } from 'http';
import jwt from 'jsonwebtoken';
import { compact } from 'lodash';
import _ from 'lodash';
import { Cookie } from 'tough-cookie';

import logger from '../../logger';
import { GET_USER } from '../../src/graphql-operations/rails/user.query';
import { getUnauthedToken } from '../../src/services/auth-service/get-unauthed-token.service';
import { UserQuery } from '../../src/ts/@types/generated/graphql/rails/graphql';

export interface UserAuthContext {
  tenantId: number;
  user?: UserQuery['user'];
}

/**
 * Helper to parse out Cookie instances from a cookie string.
 *
 * Incoming cookie from fastify request and unauthed token response
 * have different delimiters, so splitToken accounts for that.
 */
const parseCookieString = (
  cookieString: string | undefined,
  splitToken: string,
): Cookie[] => {
  if (!cookieString) {
    return [];
  }
  const cookieStrings: string[] = compact(
    cookieString.split(splitToken) || [cookieString],
  );
  return _.compact(cookieStrings.map((c) => Cookie.parse(c)));
};

interface DecodedToken {
  iat: number;
  exp: number;
  iss: string;
  tenantId: number;
  anonId: string;
  sub?: string; // sub === userId
}

/**
 * Verify and decode JWT string
 */
const decodeToken = (token: string): DecodedToken | undefined => {
  try {
    return jwt.verify(token, process.env.JWT_PUBLIC_KEY!) as DecodedToken;
  } catch (err: any) {
    // no need to log error on expired token
    if (err.message !== 'jwt expired') {
      logger.error({ msg: 'Failed to decode JWT', noticable: err });
    }
    return undefined;
  }
};

/**
 * Fetch logged in user based off existing auth headers.
 *
 * @todo in near future this should hit auth-service instead of rails
 */
const getUser = async (
  headers: IncomingHttpHeaders,
): Promise<UserQuery['user']> => {
  try {
    const resp = await request<{ user: UserQuery['user'] }>({
      // This is a server side request that happens before Next.js does all it's magic
      // so referencing `process.env` directly and ideally this is an internal API call
      // at some point in the near future.
      url: process.env.RAILS_GRAPH_URL!,
      document: GET_USER,
      requestHeaders: headers as unknown as HeadersInit,
    });
    return resp.user;
  } catch (err: any) {
    logger.debug({
      msg: 'Failed to fetch user from rails app. This is expected if the user is not logged in.',
      noticable: err,
    });

    // Rails only returns a successful response if the user is authenticated
    const isExpectedError = err.response?.errors[0]?.message?.includes(
      'Must be authenticated to query User',
    );

    if (!isExpectedError) {
      logger.error({
        msg: 'Failed to get user due to unexpected error',
        noticable: err,
      });
    }
  }
};

/**
 * Sets the `authContext` with tenantId and user object, if authed.
 *
 * Returns raw cookie strings to be set on the reply in custom server if applicable.
 */
export const getAuthContext = async (
  domain: string,
  headers: IncomingHttpHeaders,
): Promise<{
  authContext: UserAuthContext;
  cookiesToSet?: string[];
}> => {
  // Look for the cookie that holds the Platform token. If there is one then try and
  // decode and verify the token. If there token is validated then we can just it.
  const requestCookies = parseCookieString(headers['cookie'], '; ');
  const jwtCookie = requestCookies.find((c) => c.key === 'tf-token');
  let decodedToken = jwtCookie ? decodeToken(jwtCookie.value) : undefined;

  // If the token is missing or invalid, fetch a fresh anon token from Territory and
  // return the raw Set-Cookie strings so the custom server can forward them.
  if (!decodedToken) {
    const { tenantId, setCookies } = await getUnauthedToken({
      domain,
      cookieHeader: headers['cookie'],
    });

    return {
      cookiesToSet: setCookies,
      authContext: { tenantId },
    };
  } else if (decodedToken.sub) {
    // only send cookie header to avoid EPROTO error: see https://www.spektor.dev/apollo-client-and-ssl-handshake-failure/
    const userRequestHeaders: IncomingHttpHeaders = {
      cookie: headers['cookie'],
    };
    const user = await getUser(userRequestHeaders);
    return {
      authContext: {
        user,
        tenantId: decodedToken.tenantId,
      },
    };
  }

  return {
    authContext: { tenantId: decodedToken.tenantId },
  };
};
